// src/pages/Settings.tsx

/**
 * Settings page for the Quantik Mind enterprise frontend.
 *
 * This page is responsible for:
 * - loading project-scoped settings
 * - exposing user, project, observability, library, historical-data, and
 *   service account/API key tabs
 * - saving project and configuration updates
 * - enforcing the current Enterprise-only availability logic
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Typography,
  TextField,
  Chip,
  Divider,
  IconButton,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BoltIcon from '@mui/icons-material/Bolt';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/authContext';
import { useProject } from '../context/ProjectContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useLocation } from 'react-router-dom';
import NearMissTab from '../components/settings/NearMissTab';
import FunctionalTestLibraryTab from '../components/settings/FunctionalTestLibraryTab';
import HistoricalDataTab from '../components/settings/HistoricalDataTab';
import ObservabilityTab from '../components/settings/ObservabilityTab';
import ApiAccessTab from '../components/settings/ApiAccessTab';
import ActionButton from '../components/common/ActionButton';
import SupportRequestModal from '../components/SupportRequestModal';
import { SubscriptionSummary, switchTestOrganizationPlan } from '../api/subscription';
import { listFunctionalLibrary } from '../api/functionalLibraryApi';
import { api } from '../utils/apiClient';
import { formatApiError } from '../utils/formatApiError';
import { formatPercentage } from '../utils/percentage';
import {
  useProjectSettings,
  type SettingsData,
} from '../features/settings/hooks/useProjectSettings';
import { useProjectDataMode } from '../features/settings/hooks/useProjectDataMode';

const RELIABLE_SERVICE_FIELDS = [
  'service',
  'service_name',
  'serviceName',
  'target_service',
  'targetService',
  'application_service',
  'applicationService',
  'microservice',
  'app_service',
  'appService',
] as const;

const PLACEHOLDER_SERVICE_VALUES = new Set([
  'unknown',
  'n/a',
  'na',
  'none',
  'null',
  'undefined',
  '-',
]);

const normalizeLogicalServiceName = (value: unknown) => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized || PLACEHOLDER_SERVICE_VALUES.has(normalized)) {
    return null;
  }

  return normalized;
};

const extractFunctionalLibraryRows = (library: unknown): any[] => {
  if (Array.isArray(library)) {
    return library;
  }

  if (!library || typeof library !== 'object') {
    return [];
  }

  const candidates = [
    (library as any).data,
    (library as any).items,
    (library as any).rows,
    (library as any).tests,
  ];

  return candidates.find(Array.isArray) ?? [];
};

const countLogicalServicesFromLibrary = (library: unknown) => {
  const serviceNames = new Set<string>();

  extractFunctionalLibraryRows(library).forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    for (const field of RELIABLE_SERVICE_FIELDS) {
      const normalized = normalizeLogicalServiceName((item as any)[field]);
      if (normalized) {
        serviceNames.add(normalized);
        return;
      }
    }
  });

  return serviceNames.size;
};

/* ============================================================================
   Types
============================================================================ */

/**
 * Props accepted by the user profile tab.
 */
interface UserProfileTabProps {
  authData: any;
  onAuthDataUpdated: (nextAuthData: any) => void;
}

/**
 * Props accepted by the project settings tab.
 */
interface ProjectSettingsTabProps {
  settings: SettingsData;
  onSave: (updates: Partial<SettingsData>) => void;
  canEdit: boolean;
}

const getSettingsEditability = (
  authData: any,
  settings: SettingsData | null
) => {
  const isInternalAdmin = !!authData?.isInternalAdmin;
  const isOrgAdmin = !!authData?.isAdmin;
  const canEditFromBackend = !!settings?.canEditSettings;
  const canEditSettings = isInternalAdmin || isOrgAdmin || canEditFromBackend;

  return {
    canEditSettings,
    canEditObservability: canEditSettings,
    reason: canEditSettings
      ? isInternalAdmin
        ? 'internal_admin'
        : isOrgAdmin
          ? 'organization_admin'
          : canEditFromBackend
            ? 'project_owner_or_editor'
            : 'unknown'
      : 'no_matching_permission_source',
  };
};

/* ============================================================================
   Main component
============================================================================ */

/**
 * Render the main settings page.
 *
 * @returns The settings page component.
 */
const SettingsPage: React.FC = () => {
  const { authData, setAuthData } = useAuth();
  const { currentProject } = useProject();
  const {
    subscription,
    loading: subscriptionLoading,
    refreshSubscription,
  } = useSubscription();
  const location = useLocation();

  const [tab, setTab] = useState<number | string>(0);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportSuccess, setSupportSuccess] = useState('');
  const functionalTestsTabIndex = 3;
  const historicalTabIndex = 4;

  useEffect(() => {
    const initialTab = location.state?.initialTab;
    if (typeof initialTab === 'number') {
      setTab(initialTab);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'near-misses') setTab('near-misses');
    if (params.get('tab') === 'functional-tests') setTab(functionalTestsTabIndex);
    if (params.get('tab') === 'historical-data') {
      setTab(historicalTabIndex);
    }
  }, [historicalTabIndex, location.search]);

  const {
    settings,
    setSettings,
    loading,
    error,
    success,
    setSuccess,
    saveSettings,
  } = useProjectSettings(currentProject?.id);
  const { projectDataMode } = useProjectDataMode({
    projectId: currentProject?.id,
    chaosAllowed: false,
  });

  if (loading && !settings) {
    return <CircularProgress />;
  }

  if (!currentProject) {
    return (
      <Box
        className="page-transition"
        sx={{
          width: '100%',
          minWidth: 0,
          maxWidth: 1240,
          mx: 'auto',
        }}
      >
        <PageHeader
          icon={<SettingsIcon fontSize="inherit" />}
          title="Settings"
          subtitle="Manage User & Project Settings"
        />

        <Paper elevation={3} sx={{ borderRadius: 2.5, p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={2}>
            <Alert
              severity="info"
              sx={{
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              Select or create a project before configuring settings.
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <ActionButton
                href="/onboarding"
              >
                Return to onboarding
              </ActionButton>
              <ActionButton
                href="/projects"
              >
                View projects
              </ActionButton>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (error && !settings) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!settings) {
    return <Alert severity="warning">No settings found</Alert>;
  }

  const editability = getSettingsEditability(authData, settings);
  const showApiAccessTab = !!currentProject && editability.canEditSettings;
  const apiAccessTabIndex = showApiAccessTab ? 5 : -1;
  const licenseTabIndex = showApiAccessTab ? 6 : 5;

  /* ==========================================================================
     Render
  ========================================================================= */

  return (
    <Box
      className="page-transition"
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: 1240,
        mx: 'auto',
      }}
    >
      <PageHeader
        icon={<SettingsIcon fontSize="inherit" />}
        title="Settings"
        subtitle="Manage User & Project Settings"
      />

      <Paper elevation={3} sx={{ borderRadius: 2.5, overflow: 'hidden', minWidth: 0 }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'rgba(255, 255, 255, 0.02)',
            minWidth: 0,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minWidth: 0,
              maxWidth: '100%',
              '& .MuiTabs-scroller': {
                overflowX: 'auto !important',
                overflowY: 'hidden',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              },
              '& .MuiTabs-scroller::-webkit-scrollbar': {
                display: 'none',
              },
              '& .MuiTabs-flexContainer': {
                flexWrap: 'nowrap',
              },
              '& .MuiTabs-scrollButtons': {
                color: 'rgba(230, 237, 243, 0.7)',
              },
              '& .MuiTab-root': {
                minHeight: { xs: 56, md: 60 },
                textTransform: 'none',
                fontSize: { xs: '0.8125rem', md: '0.9rem' },
                fontWeight: 500,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minWidth: 'fit-content',
                px: { xs: 1.5, md: 2 },
              },
            }}
          >
            <Tab icon={<PersonIcon />} iconPosition="start" label="User Profile" />
            <Tab
              icon={<BusinessIcon />}
              iconPosition="start"
              label="Project Settings"
            />
            <Tab
              icon={<MonitorHeartIcon />}
              iconPosition="start"
              label="Observability"
            />
            <Tab
              icon={<CheckBoxIcon />}
              iconPosition="start"
              label="Functional Tests"
            />
            <Tab
              icon={<HistoryIcon />}
              iconPosition="start"
              label="Historical Data"
            />
            {showApiAccessTab && (
              <Tab
                icon={<BoltIcon />}
                iconPosition="start"
                label="Service Accounts & API Keys"
              />
            )}
            <Tab
              icon={<CheckCircleIcon />}
              iconPosition="start"
              label="License & Usage"
            />
            <Tab value="near-misses" icon={<MonitorHeartIcon />} iconPosition="start" label="Near misses" />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, minWidth: 0 }}>
          {tab === 0 && (
            <UserProfileTab
              authData={authData}
              onAuthDataUpdated={(nextAuthData) => setAuthData(nextAuthData)}
            />
          )}
          {tab === 1 && (
            <>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <ProjectSettingsTab
                settings={settings}
                onSave={(updates) => saveSettings(updates).catch(() => {
                  // The hook exposes the error above; consume the event promise.
                })}
                canEdit={editability.canEditSettings}
              />
            </>
          )}
          {tab === 2 && (
            <ObservabilityTab
              projectId={currentProject?.id}
              isAdmin={editability.canEditObservability}
              settings={settings}
              onSave={saveSettings}
              onProviderSaved={(provider, enabled) => {
                if (provider === 'prometheus' || provider === 'dynatrace') {
                  setSettings(previous => previous ? { ...previous, activeMetricsProvider: provider, observabilityEnabled: enabled } : previous);
                }
              }}
              sampleOnlyMode={projectDataMode === 'sample_only'}
            />
          )}
          {tab === functionalTestsTabIndex && currentProject && (
            <FunctionalTestLibraryTab key={currentProject.id} projectId={currentProject.id} focusTestId={new URLSearchParams(location.search).get('test') ?? undefined} />
          )}
          {tab === historicalTabIndex && (
            currentProject ? (
              <HistoricalDataTab
                projectId={currentProject.id}
                canEdit={editability.canEditSettings}
                focusType={(() => {
                  const type = new URLSearchParams(location.search).get('type');
                  return type === 'functional' ? type : null;
                })()}
                sampleOnlyMode={projectDataMode === 'sample_only'}
              />
            ) : (
              <Alert severity="info">Select a project to manage historical data.</Alert>
            )
          )}
          {showApiAccessTab && tab === apiAccessTabIndex && currentProject && (
            <ApiAccessTab
              projectId={currentProject.id}
              canManage={editability.canEditSettings}
            />
          )}
          {tab === 'near-misses' && <NearMissTab key={currentProject.id} projectId={currentProject.id}
            settings={settings} onSave={saveSettings} canEdit={editability.canEditSettings}
            evaluationId={new URLSearchParams(location.search).get('evaluation') ?? undefined} />}
          {tab === licenseTabIndex && (
            <LicenseUsageTab
              subscription={subscription}
              loading={subscriptionLoading}
              isInternalAdmin={!!authData?.isInternalAdmin}
              projectId={currentProject.id}
              onOpenSupport={() => setSupportModalOpen(true)}
              onSwitchPlan={refreshSubscription}
            />
          )}
        </Box>
      </Paper>

      <SupportRequestModal
        open={supportModalOpen}
        requesterEmail={authData?.email}
        initialCategory="commercial_license"
        onClose={() => setSupportModalOpen(false)}
        onRequestCreated={(referenceCode) => {
          setSupportModalOpen(false);
          setSupportSuccess(`Request received. Your reference is ${referenceCode}.`);
        }}
      />

      <Snackbar
        open={!!success || !!supportSuccess}
        autoHideDuration={3000}
        onClose={() => {
          setSuccess(null);
          setSupportSuccess('');
        }}
        message={success || supportSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
};

const formatLimit = (value: number | null | undefined) =>
  value === null || value === undefined ? 'Unlimited' : String(value);

const formatPlanName = (planCode?: string | null) => {
  if (!planCode) {
    return 'Unknown Plan';
  }
  if (planCode.startsWith('enterprise')) {
    return 'Enterprise';
  }
  return planCode.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatStatusLabel = (status?: string | null) => {
  if (!status) {
    return 'Unknown';
  }

  return status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatBillingLabel = (billingInterval?: string | null) => {
  if (!billingInterval) {
    return 'Custom';
  }

  return billingInterval.charAt(0).toUpperCase() + billingInterval.slice(1);
};

const formatPeriod = (value?: string | null) => {
  if (!value) {
    return 'Not available';
  }

  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const getUsageProgress = (used: number, limit: number | null | undefined) => {
  if (limit === null || limit === undefined || limit <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (used / limit) * 100));
};

const getUsageBarColors = (progress: number) => {
  if (progress >= 100) {
    return {
      bar: 'linear-gradient(90deg, #FF5D5D 0%, #FF8E7E 100%)',
      glow: 'rgba(255, 93, 93, 0.24)',
    };
  }
  if (progress >= 95) {
    return {
      bar: 'linear-gradient(90deg, #FF7B7B 0%, #FFAB7B 100%)',
      glow: 'rgba(255, 123, 123, 0.22)',
    };
  }
  if (progress >= 80) {
    return {
      bar: 'linear-gradient(90deg, #FFBE5C 0%, #FFD678 100%)',
      glow: 'rgba(255, 190, 92, 0.2)',
    };
  }
  return {
    bar: 'linear-gradient(90deg, #39FF14 0%, #73FF5B 100%)',
    glow: 'rgba(57, 255, 20, 0.18)',
  };
};

const UsageMetricCard: React.FC<{
  eyebrow: string;
  title: string;
  used: number;
  limit: number | null | undefined;
  remaining?: number | null;
  helperText?: string | null;
  valueSuffix?: string;
  accent?: 'green' | 'blue';
}> = ({ eyebrow, title, used, limit, remaining, helperText, valueSuffix, accent = 'green' }) => {
  const progress = getUsageProgress(used, limit);
  const accentColor = accent === 'blue' ? '#67D9FF' : '#39FF14';
  const accentGlow =
    accent === 'blue' ? 'rgba(0, 180, 255, 0.18)' : 'rgba(57, 255, 20, 0.18)';
  const progressColors = getUsageBarColors(progress);

  return (
    <Card
      className="enterprise-card"
      sx={{
        height: '100%',
        minWidth: 0,
        borderRadius: 3,
        background:
          'linear-gradient(180deg, rgba(15, 21, 30, 0.96) 0%, rgba(10, 14, 20, 0.98) 100%)',
        borderColor: 'rgba(255,255,255,0.08)',
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.03), 0 12px 28px ${accentGlow}`,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.25 } }}>
        <Stack spacing={1.75}>
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: 'rgba(230, 237, 243, 0.6)',
                letterSpacing: '0.12em',
                fontWeight: 700,
                fontSize: '0.7rem',
              }}
            >
              {eyebrow}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.25 }}>
              {title}
            </Typography>
          </Box>

          {limit === null || limit === undefined ? (
            <Stack spacing={1}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  border: `1px solid ${accentGlow}`,
                  bgcolor: 'rgba(255,255,255,0.02)',
                }}
              >
                <AllInclusiveIcon sx={{ color: accentColor }} />
              </Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>
                Unlimited
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {used} used this period
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.25}>
              <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1 }}>
                {used}
                <Typography
                  component="span"
                  variant="body1"
                  sx={{ color: 'text.secondary', ml: 1, fontWeight: 600 }}
                >
                  / {limit}{valueSuffix ? ` ${valueSuffix}` : ''}
                </Typography>
              </Typography>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 999,
                    bgcolor: 'rgba(255,255,255,0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    background: progressColors.bar,
                    boxShadow: `0 0 18px ${progressColors.glow}`,
                  },
                }}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                rowGap={1}
              >
                <Typography variant="caption" color="text.secondary">
                  {formatPercentage(progress)} utilized
                </Typography>
                <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                  {remaining ?? Math.max(limit - used, 0)} remaining
                </Typography>
              </Stack>
              {helperText && (
                <Typography variant="caption" color="text.secondary">
                  {helperText}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const FeatureAccessCard: React.FC<{
  label: string;
  description: string;
  enabled: boolean;
}> = ({ label, description, enabled }) => (
  <Card
    className="enterprise-card"
    sx={{
      height: '100%',
      minWidth: 0,
      borderRadius: 2.5,
      background: enabled
        ? 'linear-gradient(180deg, rgba(16, 25, 18, 0.98) 0%, rgba(12, 18, 14, 0.98) 100%)'
        : 'linear-gradient(180deg, rgba(24, 19, 15, 0.98) 0%, rgba(15, 16, 20, 0.98) 100%)',
      borderColor: enabled ? 'rgba(57, 255, 20, 0.22)' : 'rgba(255, 190, 92, 0.18)',
      boxShadow: enabled
        ? '0 14px 30px rgba(57, 255, 20, 0.08)'
        : '0 14px 30px rgba(255, 190, 92, 0.06)',
    }}
  >
    <CardContent sx={{ p: 2 }}>
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 0 }}>
            {label}
          </Typography>
          <Chip
            size="small"
            icon={enabled ? <BoltIcon /> : <LockOutlinedIcon />}
            label={enabled ? 'Enabled' : 'Locked'}
            sx={{
              color: enabled ? '#DFFFE6' : '#F5D7A1',
              bgcolor: enabled ? 'rgba(57, 255, 20, 0.12)' : 'rgba(255, 190, 92, 0.12)',
              border: enabled
                ? '1px solid rgba(57, 255, 20, 0.2)'
                : '1px solid rgba(255, 190, 92, 0.2)',
              '& .MuiChip-icon': {
                color: enabled ? '#39FF14' : '#FFBE5C',
              },
            }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          {description}
        </Typography>
      </Stack>
    </CardContent>
  </Card>
);

const LicenseUsageTab: React.FC<{
  subscription: SubscriptionSummary | null;
  loading: boolean;
  isInternalAdmin: boolean;
  projectId: string;
  onOpenSupport: () => void;
  onSwitchPlan: () => Promise<void>;
}> = ({ subscription, loading, isInternalAdmin, projectId, onOpenSupport, onSwitchPlan }) => {
  const [planSwitching, setPlanSwitching] = useState(false);
  const [planSwitchError, setPlanSwitchError] = useState<string | null>(null);
  const [logicalServiceCount, setLogicalServiceCount] = useState<number | null>(null);

  const publicPlan = subscription?.public_plan_code ?? 'free';
  const ctaLabel =
    publicPlan === 'free'
      ? 'Contact us'
      : publicPlan === 'starter'
      ? 'Upgrade to Team'
      : publicPlan === 'team'
      ? 'Upgrade to Business'
      : 'Contact support';

  const ctaText =
    publicPlan === 'free'
      ? "Need more capacity or broader coverage? Contact us and we'll help you choose the right setup."
      : publicPlan === 'starter'
      ? 'Upgrade to Team for more monthly test selections, higher service capacity, and full delivery workflow support.'
      : publicPlan === 'team'
      ? 'Upgrade to Business if you need more scale across active CI/CD workflows and connected services.'
      : publicPlan === 'business'
      ? 'Need more scale or contract flexibility? Talk to us about Enterprise.'
      : 'Your organization is on an enterprise contract with included usage and overage handling.';

  useEffect(() => {
    let cancelled = false;

    const loadLogicalServiceCount = async () => {
      if (!projectId) {
        setLogicalServiceCount(null);
        return;
      }

      try {
        const library = await listFunctionalLibrary(projectId);
        if (!cancelled) {
          setLogicalServiceCount(countLogicalServicesFromLibrary(library));
        }
      } catch {
        if (!cancelled) {
          setLogicalServiceCount(null);
        }
      }
    };

    void loadLogicalServiceCount();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handlePlanSwitch = async (
    nextPlan:
      | 'free'
      | 'starter'
      | 'team'
      | 'business'
      | 'enterprise_s'
      | 'enterprise_m'
      | 'enterprise_l'
  ) => {
    setPlanSwitching(true);
    setPlanSwitchError(null);

    try {
      await switchTestOrganizationPlan(nextPlan);
      await onSwitchPlan();
    } catch (err) {
      setPlanSwitchError(formatApiError(err, 'Unable to switch the test organization plan.'));
    } finally {
      setPlanSwitching(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            License & Usage
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Monitor plan access, usage capacity, and support options for your organization.
          </Typography>
        </Box>
        <Chip
          icon={<AutoAwesomeIcon />}
          label="Subscription Health"
          sx={{
            color: '#DFFFE6',
            bgcolor: 'rgba(57, 255, 20, 0.12)',
            border: '1px solid rgba(57, 255, 20, 0.2)',
            '& .MuiChip-icon': { color: '#39FF14' },
          }}
        />
      </Box>

      {loading && <CircularProgress size={24} />}

      {!loading && !subscription && (
        <Alert severity="warning">Subscription details are not available yet.</Alert>
      )}

      {!loading && subscription && (
        <Stack spacing={3}>
          <Card
            className="enterprise-card"
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              borderColor: 'rgba(57, 255, 20, 0.18)',
              background:
                'radial-gradient(circle at top right, rgba(57, 255, 20, 0.14), transparent 34%), linear-gradient(180deg, rgba(16, 20, 27, 0.98) 0%, rgba(10, 14, 20, 0.98) 100%)',
              boxShadow: '0 18px 42px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(57, 255, 20, 0.08)',
              minWidth: 0,
            }}
          >
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Grid container spacing={2.5} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Stack spacing={1.5}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'rgba(230, 237, 243, 0.65)',
                        letterSpacing: '0.14em',
                        fontWeight: 700,
                      }}
                    >
                      Plan Overview
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          letterSpacing: '-0.03em',
                          color: '#fff',
                          fontSize: { xs: '1.75rem', md: '2.125rem' },
                        }}
                      >
                        {subscription.plan_label || formatPlanName(subscription.plan_code)}
                      </Typography>
                      <Chip
                        label={formatStatusLabel(subscription.status)}
                        size="small"
                        sx={{
                          color: '#DFFFE6',
                          bgcolor: 'rgba(57, 255, 20, 0.12)',
                          border: '1px solid rgba(57, 255, 20, 0.2)',
                        }}
                      />
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ maxWidth: 560, lineHeight: 1.6 }}
                    >
                      {subscription.plan_code === 'free'
                        ? 'Self-service access with functional selection, a single project, and a focused monthly quota.'
                        : subscription.plan_code === 'team'
                        ? 'Collaborative capacity for engineering teams with multi-project workflows and expanded run volume.'
                        : 'Contract-backed access designed for large-scale usage, broad feature coverage, and flexible capacity.'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Stack
                    spacing={1.5}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      minWidth: 0,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Billing Interval
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {formatBillingLabel(subscription.billing_cycle || subscription.billing_interval)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Current Period
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatPeriod(subscription.period_start)} - {formatPeriod(subscription.period_end)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Reset date
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatPeriod(subscription.reset_date)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Usage Capacity
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.75, display: 'block' }}>
              Track monthly test selections and connected services for the current usage window.
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <UsageMetricCard
                  eyebrow="Service Coverage"
                  title="Services"
                  used={logicalServiceCount ?? 0}
                  limit={subscription.services_limit}
                  remaining={
                    subscription.services_limit === null
                      ? null
                      : Math.max(
                          subscription.services_limit - (logicalServiceCount ?? 0),
                          0
                        )
                  }
                  helperText={
                    subscription.services_limit !== null &&
                    (logicalServiceCount ?? 0) > subscription.services_limit
                      ? 'Service coverage exceeded. Contact support to expand coverage.'
                      : logicalServiceCount === null
                      ? 'Logical service metadata is not available for this project.'
                      : 'Application services covered by this project.'
                  }
                  valueSuffix="services"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <UsageMetricCard
                  eyebrow="Test Selections"
                  title="Monthly Selection Capacity"
                  used={subscription.test_selections_current}
                  limit={subscription.test_selections_limit}
                  remaining={subscription.remaining_test_selections}
                  helperText={
                    subscription.overage_enabled && subscription.overage_selections_current > 0
                      ? `+${subscription.overage_selections_current.toLocaleString()} overage selections this month`
                      : null
                  }
                  accent="blue"
                />
              </Grid>
            </Grid>
            {subscription.overage_enabled && (
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  bgcolor: 'rgba(0, 180, 255, 0.08)',
                  border: '1px solid rgba(0, 180, 255, 0.18)',
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  Test Selections — {formatPeriod(subscription.period_start)} to {formatPeriod(subscription.period_end)}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  {`${(subscription.test_selections_limit || 0).toLocaleString()} / ${(subscription.test_selections_limit || 0).toLocaleString()} base included`}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  {`+ ${subscription.overage_selections_current.toLocaleString()} overage selections this month`}
                </Typography>
                {subscription.estimated_overage_charge_usd !== null && (
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    {`Estimated overage charge: $${subscription.estimated_overage_charge_usd.toFixed(2)}`}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Feature Access
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.75, display: 'block' }}>
              Entitlements update automatically based on the current organization plan.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FeatureAccessCard
                  label="Functional Selection"
                  description="Core intelligent test selection workflows are available."
                  enabled={subscription.features.functional_selection}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FeatureAccessCard
                  label="Full History"
                  description="Access the complete organization run history without a time cap."
                  enabled={subscription.features.full_history}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FeatureAccessCard
                  label="Recent History"
                  description={
                    subscription.features.full_history
                      ? 'Recent history stays available as part of full history access.'
                      : `Enabled · ${subscription.features.free_history_days || 7} days of recent decision history.`
                  }
                  enabled={
                    subscription.features.full_history ||
                    !!subscription.features.free_history_days
                  }
                />
              </Grid>
            </Grid>
          </Box>

          <Card
            className="enterprise-card"
            sx={{
              borderRadius: 4,
              background:
                'linear-gradient(135deg, rgba(18, 23, 31, 0.98) 0%, rgba(12, 16, 24, 0.98) 100%)',
              borderColor:
                publicPlan === 'free'
                  ? 'rgba(57, 255, 20, 0.22)'
                  : 'rgba(255,255,255,0.08)',
              minWidth: 0,
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Box sx={{ maxWidth: 700 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    Plan Actions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {ctaText}
                  </Typography>
                </Box>
                <ActionButton
                  onClick={onOpenSupport}
                  sx={{ minWidth: 148 }}
                >
                  {ctaLabel}
                </ActionButton>
              </Stack>
            </CardContent>
          </Card>

          {isInternalAdmin && (
            <Card
              className="enterprise-card"
              sx={{
                borderRadius: 4,
                background:
                  'linear-gradient(135deg, rgba(18, 23, 31, 0.98) 0%, rgba(12, 16, 24, 0.98) 100%)',
                borderColor: 'rgba(255,255,255,0.08)',
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                      Test organization plan
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Internal testing only. Does not affect billing.
                    </Typography>
                  </Box>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel id="test-plan-select-label">Plan</InputLabel>
                      <Select
                        labelId="test-plan-select-label"
                        label="Plan"
                        value={subscription.plan_code}
                        disabled={planSwitching}
                        onChange={(event) =>
                          void handlePlanSwitch(
                            event.target.value as
                              | 'free'
                              | 'starter'
                              | 'team'
                              | 'business'
                              | 'enterprise_s'
                              | 'enterprise_m'
                              | 'enterprise_l'
                          )
                        }
                      >
                        <MenuItem value="free">Free</MenuItem>
                        <MenuItem value="starter">Starter</MenuItem>
                        <MenuItem value="team">Team</MenuItem>
                        <MenuItem value="business">Business</MenuItem>
                        <MenuItem value="enterprise_s">Enterprise S</MenuItem>
                        <MenuItem value="enterprise_m">Enterprise M</MenuItem>
                        <MenuItem value="enterprise_l">Enterprise L</MenuItem>
                      </Select>
                    </FormControl>
                    {planSwitching && <CircularProgress size={20} />}
                  </Stack>

                  {planSwitchError && (
                    <Alert
                      severity="error"
                      sx={{
                        bgcolor: 'rgba(120, 18, 18, 0.16)',
                        border: '1px solid rgba(255, 120, 120, 0.18)',
                      }}
                    >
                      {planSwitchError}
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}
    </Stack>
  );
};

/* ============================================================================
   Tab 1: User profile
============================================================================ */

/**
 * Render the user profile tab.
 *
 * @param props - User profile tab props.
 * @returns The user profile tab.
 */
const UserProfileTab: React.FC<UserProfileTabProps> = ({ authData, onAuthDataUpdated }) => {
  const [fullName, setFullName] = useState(authData?.fullName || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setFullName(authData?.fullName || '');
  }, [authData?.fullName]);

  /**
   * Persist the updated full name for the current user.
   */
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.patch('/auth/me', { full_name: fullName });

      setSuccess('Profile updated successfully.');
      setIsEditing(false);

      const authDataUpdated = {
        ...authData,
        fullName: response.full_name || fullName,
        organizationName: response.organization_name || authData?.organizationName,
        organizationSlug: response.organization_slug || authData?.organizationSlug,
        isAdmin: response.admin ?? authData?.isAdmin,
      };
      onAuthDataUpdated(authDataUpdated);
    } catch (err: any) {
      setError(formatApiError(err, 'Update failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6" fontWeight={600}>
        User Profile
      </Typography>

      <Card className="enterprise-card">
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Personal Information
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Full Name
                  </Typography>

                  {isEditing ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        value={fullName}
                        onChange={(event) => setFullName(event.target.value)}
                        fullWidth
                        size="small"
                        placeholder="John Doe"
                      />
                      <ActionButton
                        onClick={() => void handleSave()}
                        disabled={saving}
                        size="small"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </ActionButton>
                      <ActionButton
                        onClick={() => {
                          setIsEditing(false);
                          setFullName(authData?.fullName || '');
                        }}
                        size="small"
                      >
                        Cancel
                      </ActionButton>
                    </Stack>
                  ) : (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography>{fullName || 'Not set'}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => setIsEditing(true)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Email
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                      {authData?.email || ''}
                    </Typography>
                    <Chip
                      label="Primary Identity"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" mt={0.5}>
                    Your sign-in email cannot be changed from settings.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Username
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={authData?.username}
                      variant="outlined"
                      sx={{ fontFamily: 'monospace' }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Organization
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  label={authData?.organizationName}
                  color="primary"
                  variant="outlined"
                  sx={{ maxWidth: '100%' }}
                />
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Role & Permissions
              </Typography>
              <Chip
                label={authData?.isAdmin ? 'Admin' : 'User'}
                color={authData?.isAdmin ? 'success' : 'default'}
                icon={<CheckCircleIcon />}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
              >
                {authData?.isAdmin
                  ? 'Full access to organization settings and user management'
                  : 'Standard user access'}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Security
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Password
                  </Typography>
                  <ActionButton disabled>
                    Change Password (Coming Soon)
                  </ActionButton>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Two-Factor Authentication
                  </Typography>
                  <ActionButton disabled>
                    Enable 2FA (Coming Soon)
                  </ActionButton>
                </Box>
              </Stack>
            </Box>

            {success && (
              <Alert severity="success" onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

/* ============================================================================
   Tab 2: Project settings
============================================================================ */

/**
 * Render the project settings tab.
 *
 * @param props - Project settings tab props.
 * @returns The project settings tab.
 */
const ProjectSettingsTab: React.FC<ProjectSettingsTabProps> = ({
  settings,
  onSave,
  canEdit,
}) => {
  const [projectName, setProjectName] = useState(settings.projectName);
  const [systemUrl, setSystemUrl] = useState(settings.testedSystemUrl);
  const [environment, setEnvironment] = useState(settings.environment);
  const [testLibraryEnabled, setTestLibraryEnabled] = useState(settings.testLibraryEnabled);
  const [historicalDataEnabled, setHistoricalDataEnabled] = useState(settings.historicalDataEnabled);

  /**
   * Keep the local form state aligned when the persisted settings payload
   * changes.
   */
  React.useEffect(() => {
    setProjectName(settings.projectName);
    setSystemUrl(settings.testedSystemUrl);
    setEnvironment(settings.environment);
    setTestLibraryEnabled(settings.testLibraryEnabled);
    setHistoricalDataEnabled(settings.historicalDataEnabled);
  }, [settings]);

  return (
    <Stack spacing={4}>
      <Typography variant="h6" fontWeight={600}>
        Project Configuration
      </Typography>

      <Card className="enterprise-card">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Project Information
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Project Name"
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              disabled={!canEdit}
              fullWidth
            />

            <TextField
              label="Tested System URL"
              value={systemUrl}
              onChange={(event) => setSystemUrl(event.target.value)}
              disabled={!canEdit}
              fullWidth
              placeholder="https://api.example.com"
            />

            <FormControl fullWidth disabled={!canEdit}>
              <InputLabel>Environment</InputLabel>
              <Select
                value={environment}
                label="Environment"
                onChange={(event) => setEnvironment(event.target.value)}
              >
                <MenuItem value="development">Development</MenuItem>
                <MenuItem value="staging">Staging</MenuItem>
                <MenuItem value="production">Production</MenuItem>
              </Select>
            </FormControl>

            {canEdit && (
              <ActionButton
                onClick={() =>
                  onSave({
                    projectName,
                    testedSystemUrl: systemUrl,
                    environment,
                  })
                }
              >
                Save Project Info
              </ActionButton>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card className="enterprise-card">
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Project Signal Sources
          </Typography>

          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={testLibraryEnabled}
                  onChange={(event) => setTestLibraryEnabled(event.target.checked)}
                  disabled={!canEdit}
                />
              }
              label="Functional test library enabled"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={historicalDataEnabled}
                  onChange={(event) => setHistoricalDataEnabled(event.target.checked)}
                  disabled={!canEdit}
                />
              }
              label="Historical execution data enabled"
            />
            {canEdit && (
              <ActionButton
                onClick={() =>
                  onSave({
                    testLibraryEnabled,
                    historicalDataEnabled,
                  })
                }
              >
                Save Signal Sources
              </ActionButton>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default SettingsPage;
